import { Tag } from 'lucide-react';
import { useState } from 'react';

import { TagType } from '../../redux/Slices/tags';
import { useAppSelector } from '../../redux/store/hooks';
import { useLazyFetchTagByIdQuery } from '../../services/tagsApi';
import { cn } from '../../utils/tailwindMerge';
import Tooltip from '../Common/ToolTip';

import StudentsDialog from './StudentsDialog';
import { Student } from './TagCreationDialog';

const TagsTable = ({
  onDelete,
  onEdit,
  associatedStudents
}: {
  onDelete: (id: string) => void;
  onEdit: (tag: TagType) => void;
  associatedStudents: Student[];
}) => {
  const { tags } = useAppSelector(state => state.root.tags);
  const [fetchTagById] = useLazyFetchTagByIdQuery();
  const [isStudentsDialogOpen, setIsStudentsDialogOpen] = useState(false);
  const [studentsList, setStudentsList] = useState<Student[]>([]);

  const handleClickStudentCount = async (tag: TagType) => {
    if (tag.studentsCount > 0) {
      try {
        const tagRes = await fetchTagById(tag.id).unwrap();
        const studentIds: string[] = tagRes?.data || [];
        const allStudents = associatedStudents || [];
        const matchedStudents = allStudents.filter(student =>
          studentIds.includes(student.studentId)
        );
        setStudentsList(matchedStudents);
        setIsStudentsDialogOpen(true);
      } catch (err) {
        console.error('Failed to fetch associated students:', err);
      }
    }
  };

  return (
    <div className='rounded-2xl border border-blue-200 bg-white shadow-sm'>
      <div className='border-b border-gray-200 p-6'>
        <div className='flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100'>
            <Tag className='h-5 w-5 text-blue-600' />
          </div>
          <div>
            <h2 className='text-base font-bold text-gray-900'>All Batches</h2>
            <p className='text-sm font-bold text-gray-900'>{tags.length}</p>
          </div>
        </div>
      </div>

      {tags.length === 0 ? (
        <div className='p-12 text-center'>
          <Tag className='mx-auto mb-3 h-12 w-12 text-gray-300' />
          <p className='text-gray-500'>No Batches created yet</p>
          <p className='mt-1 text-sm text-gray-400'>
            Create your first Batch to get started
          </p>
        </div>
      ) : (
        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                  Batch Name
                </th>
                <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                  Description
                </th>
                <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                  Students
                </th>
                <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600'>
                  Created Date
                </th>
                {/* <th className='px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600'>
                  Actions
                </th> */}
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {tags?.map(tag => (
                <tr
                  key={tag?.id}
                  className='transition-colors hover:bg-gray-50'
                >
                  <td className='px-6 py-4'>
                    <div className='flex items-center gap-2'>
                      <div className='rounded-lg px-3 py-1'>
                        <span className='text-sm text-gray-600'>
                          {tag?.tagName}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className=' px-6 py-4'>
                    <div>
                      <span
                        data-tooltip-id={tag?.id}
                        data-tooltip-content={tag?.description}
                        className='line-clamp-1 text-sm text-gray-600'
                      >
                        {tag?.description?.slice(0, 30) || '-'}
                      </span>
                      <Tooltip id={tag?.id || ''} />
                    </div>
                  </td>

                  <td className='px-6 py-4'>
                    <div
                      onClick={() => handleClickStudentCount(tag)}
                      className={cn(
                        tag?.studentsCount > 0 &&
                          'cursor-pointer underline hover:text-blue-500 ',
                        'text-sm text-gray-900 '
                      )}
                    >
                      {tag?.studentsCount ?? '-'}
                    </div>
                  </td>
                  <td className='px-6 py-4'>
                    <span className='text-sm text-gray-600'>
                      {new Date(tag?.createdDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </td>
                  {/* <td className='px-6 py-4'>
                    <div className='flex justify-end gap-2'>
                      <button
                        onClick={() => onEdit(tag)}
                        className='rounded-lg p-2 text-blue-600 hover:bg-blue-50'
                        title='Edit'
                      >
                        <Edit2 className='h-4 w-4' />
                      </button>
                      <button
                        onClick={() => onDelete(tag?.id)}
                        className='rounded-lg p-2 text-red-600 hover:bg-red-50'
                        title='Delete'
                      >
                        <Trash2 className='h-4 w-4' />
                      </button>
                    </div>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <StudentsDialog
        isOpen={isStudentsDialogOpen}
        setIsOpen={setIsStudentsDialogOpen}
        studentsList={studentsList}
      />
    </div>
  );
};

export default TagsTable;
